// A dedicated timer component for rest periods
import React, { useEffect, useState, useRef } from "react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Timer } from "@/components/ui/Timer";
import { formatTime } from "@/utils/formatTime";
import { Button } from "@/components/ui/button";

interface RestTimerProps {
  currentRestTime: number;
  handleRestComplete: () => void;
  skipRestCommand: () => void;
  isLastSet: boolean;
  currentSetIndex: number;
  totalSets: number;
  nextExerciseName: string;
  soundEnabled: boolean;
  playSound: (type: "timer" | "rest" | "countdown" | "complete") => void;
  isRestAfterExercise: boolean;
  disabled?: boolean;
}

export default function RestTimer({
  currentRestTime,
  handleRestComplete,
  skipRestCommand,
  isLastSet,
  currentSetIndex,
  totalSets,
  nextExerciseName,
  soundEnabled,
  playSound,
  isRestAfterExercise,
  disabled = false,
}: RestTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState(currentRestTime);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasPlayedCompletionSound = useRef(false);
  const isCompletingRef = useRef(false);

  // Reset timer when currentRestTime changes
  useEffect(() => {
    setTimeRemaining(currentRestTime);
    hasPlayedCompletionSound.current = false;
    isCompletingRef.current = false;

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentRestTime]);

  // Timer effect
  useEffect(() => {
    // Pause timer if disabled
    if (disabled) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (timeRemaining <= 0) {
      // Timer complete
      if (
        soundEnabled &&
        !hasPlayedCompletionSound.current &&
        !isCompletingRef.current
      ) {
        console.log("Playing rest completion sound");
        try {
          playSound("rest");
          hasPlayedCompletionSound.current = true;
        } catch (error) {
          console.error("Error playing rest sound:", error);
        }
      }

      // Prevent multiple calls to handleRestComplete
      if (!isCompletingRef.current) {
        isCompletingRef.current = true;

        // Small delay to ensure sound plays before completing
        setTimeout(() => {
          handleRestComplete();
        }, 100);
      }

      return;
    }

    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        const newValue = prev - 1;

        // Play countdown sound for last 3 seconds
        if (newValue <= 3 && newValue > 0 && soundEnabled) {
          console.log(`Playing countdown sound at ${newValue} seconds`);
          try {
            playSound("countdown");
          } catch (error) {
            console.error("Error playing countdown sound:", error);
          }
        }

        return newValue;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeRemaining, handleRestComplete, soundEnabled, playSound, disabled]);

  // Calculate progress percentage
  const progressPercentage = Math.max(
    0,
    (timeRemaining / currentRestTime) * 100
  );

  // Handle skip rest button click
  const handleSkipRest = () => {
    // Play sound when skipping rest
    if (soundEnabled && !hasPlayedCompletionSound.current) {
      try {
        playSound("rest");
        hasPlayedCompletionSound.current = true;
      } catch (error) {
        console.error("Error playing rest sound:", error);
      }
    }

    skipRestCommand();
  };

  return (
    <>
      <Card className="mb-4 border-blue-200 dark:border-blue-800">
        <CardHeader className="bg-blue-50 dark:bg-blue-900/20 pb-2">
          <CardTitle className="text-center text-blue-700 dark:text-blue-300">
            Rest
          </CardTitle>
          {isRestAfterExercise ? (
            <div className="text-blue-400 text-md font-semibold mt-2">
              Next: {nextExerciseName}
            </div>
          ) : (
            <div className="text-blue-400 text-md font-semibold mt-2">
              Get ready for Set {currentSetIndex + 1} of {totalSets}
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-4">
          <div className="text-center">
            <div className="text-5xl font-bold mb-4">
              {formatTime(timeRemaining)}
            </div>
            <div className="w-full bg-blue-100 dark:bg-blue-900/30 h-2 rounded-full mb-4">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <Button
              onClick={handleSkipRest}
              variant="outline"
              className="w-full"
              disabled={disabled}
            >
              Skip Rest
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="text-center text-sm text-gray-500">
        <p>Next: {nextExerciseName}</p>
      </div>
    </>
  );
}
